import { useState, useEffect } from "react";
import { useAuth, apiClient } from "@/lib/api";
import { FileText, Video, Crown, Gift, Clock, ShoppingBag, RefreshCw } from "lucide-react";
import { useNavigate } from "react-router-dom";

const tierColors = {
  bronze: "from-amber-700 to-amber-500",
  silver: "from-gray-400 to-gray-300",
  gold: "from-yellow-500 to-yellow-300",
  platinum: "from-purple-400 to-pink-300"
};

const tierIcons = {
  bronze: "🥉",
  silver: "🥈", 
  gold: "🥇",
  platinum: "💎"
};

// Human-friendly plan display names
// These should ideally come from backend/admin config
const planDisplayNames = {
  free_trial: "Trial",
  starter: "Starter",
  growth: "Growth",
  pro: "Pro",
  enterprise: "Enterprise",
  admin: "Admin"
};

const getPlanDisplayName = (planId) => {
  if (!planId) return "Trial";
  return planDisplayNames[planId] || planId.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

export default function UserStatusBar() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [creditSummary, setCreditSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    if (user) {
      fetchCreditSummary();
    }
  }, [user]);
  
  const fetchCreditSummary = async () => {
    try {
      const res = await apiClient.get("/users/credit-summary");
      setCreditSummary(res.data);
    } catch (err) {
      // Fallback to user object data
      setCreditSummary(null);
    } finally {
      setLoading(false);
    }
  };
  
  if (!user) return null;
  
  // Use credit pool data if available, otherwise fallback to user object
  const totalPosts = creditSummary?.total_posts ?? (user.posts_remaining || 0);
  const planPosts = creditSummary?.plan_posts ?? (user.posts_remaining || 0);
  const paygPosts = creditSummary?.payg_posts ?? 0;
  const totalVideoCredits = creditSummary?.total_video_credits ?? ((user.video_credits_remaining || 0) + (user.video_credits_payg || 0));
  const totalRegen = creditSummary?.total_regen_credits ?? (user.regen_credits || 0);
  const hasVideoAccess = creditSummary?.has_720p_access || creditSummary?.has_1080p_access;
  const videoQuality = creditSummary?.has_1080p_access ? "1080p" : creditSummary?.has_720p_access ? "720p" : (user.video_quality_access || null);
  const activePlansCount = creditSummary?.active_plans_count ?? 1;
  const soonestExpiring = creditSummary?.soonest_expiring_plan;
  
  // Determine loyalty tier based on total spent
  const getLoyaltyTier = () => {
    const spent = user.total_spent || 0;
    if (spent >= 5000) return "platinum";
    if (spent >= 2500) return "gold";
    if (spent >= 1000) return "silver";
    if (spent >= 100) return "bronze";
    return null;
  };
  
  const tier = getLoyaltyTier();
  
  // Get expiry info from credit pool soonest expiring plan
  const getExpiryInfo = () => {
    if (soonestExpiring?.days_remaining !== undefined) {
      const daysLeft = soonestExpiring.days_remaining;
      if (daysLeft <= 0) return { daysLeft: 0, text: "Expired", urgent: true };
      if (daysLeft <= 7) return { daysLeft, text: `${daysLeft}d`, urgent: true };
      if (daysLeft <= 30) return { daysLeft, text: `${daysLeft}d`, urgent: false };
      return null; // Don't show if more than 30 days
    }
    return null;
  };
  
  const expiryInfo = getExpiryInfo();
  const isLowCredits = totalPosts <= 5;
  
  return (
    <div className="bg-white/5 border-b border-white/10 px-4 py-2">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        {/* Left side - Credits */}
        <div className="flex items-center gap-3 text-sm flex-wrap">
          {/* Plan Credits */}
          <div className="flex items-center gap-3 px-3 py-1 rounded-lg bg-white/5">
            <Crown className="w-4 h-4 text-viraly-orange" />
            <div className="flex items-center gap-2">
              <span className="text-viraly-muted">Plan:</span>
              <span className={`font-bold ${isLowCredits ? 'text-red-400' : 'text-viraly-orange'}`}>{totalPosts}</span>
              <span className="text-viraly-muted text-xs">posts</span>
            </div>
            <div className="w-px h-4 bg-white/20" />
            <div className="flex items-center gap-1" title={videoQuality ? `Quality: ${videoQuality}` : 'No video access'}>
              <Video className="w-3 h-3 text-blue-400" />
              <span className="text-blue-400 font-bold">{totalVideoCredits}</span>
              <span className="text-viraly-muted text-xs">vid</span>
              {videoQuality && (
                <span className="text-xs text-blue-400/60 ml-1">({videoQuality})</span>
              )}
            </div>
            <div className="w-px h-4 bg-white/20" />
            <div className="flex items-center gap-1">
              <Gift className="w-3 h-3 text-purple-400" />
              <span className="text-purple-400 font-bold">{totalRegen}</span>
              <span className="text-viraly-muted text-xs">regen</span>
            </div>
          </div>
          
          {/* Active plans indicator - show individual plans */}
          {creditSummary?.active_plans?.length > 0 && (
            <div className="flex items-center gap-1">
              {creditSummary.active_plans.slice(0, 3).map((plan, idx) => (
                <button
                  key={plan.id || idx}
                  onClick={() => navigate('/settings?tab=billing')}
                  className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs transition-colors ${
                    plan.days_remaining <= 7 
                      ? 'bg-red-500/10 text-red-400 hover:bg-red-500/20' 
                      : plan.days_remaining <= 14
                        ? 'bg-yellow-500/10 text-yellow-400 hover:bg-yellow-500/20'
                        : 'bg-green-500/10 text-green-400 hover:bg-green-500/20'
                  }`}
                  title={`${plan.plan_name}: ${plan.posts_remaining} posts, ${plan.video_credits_remaining} videos - expires ${new Date(plan.expires_at).toLocaleDateString()}`}
                >
                  <span className="font-medium">{plan.plan_name?.replace(' (Migrated)', '')}</span>
                  <span className="opacity-70">{plan.days_remaining}d</span>
                </button>
              ))}
              {creditSummary.active_plans.length > 3 && (
                <span className="text-xs text-viraly-muted">+{creditSummary.active_plans.length - 3}</span>
              )}
            </div>
          )}
        </div>
        
        {/* Right side - Plan & Tier */}
        <div className="flex items-center gap-3">
          {/* Loyalty Tier Badge */}
          {tier && (
            <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full bg-gradient-to-r ${tierColors[tier]} bg-opacity-20`}>
              <span className="text-sm">{tierIcons[tier]}</span>
              <span className="text-xs font-medium capitalize text-white">{tier}</span>
            </div>
          )}
          
          {/* Current Plan */}
          <button
            onClick={() => navigate('/pricing')}
            className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-viraly-orange/20 hover:bg-viraly-orange/30 transition-colors"
          >
            <Crown className="w-3.5 h-3.5 text-viraly-orange" />
            <span className="text-xs font-medium text-viraly-orange">
              {getPlanDisplayName(user.plan)}
            </span>
          </button>
          
          {/* Urgent alerts */}
          {(isLowCredits || (expiryInfo && expiryInfo.urgent)) && (
            <button
              onClick={() => navigate('/pricing')}
              className="text-xs px-2 py-1 rounded bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors animate-pulse"
            >
              {expiryInfo?.urgent && expiryInfo.daysLeft <= 0 ? 'Renew Now' : 
               expiryInfo?.urgent ? `Expires in ${expiryInfo.daysLeft}d` : 'Low Credits'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
